import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Check, CircleX, Loader2, Play, Square } from 'lucide-react';
import type { AgentBuilderApplyResult, AgentBuilderChange } from '@/lib/api';
import { changeSubject, describeChange, formatRun, type DiffRow } from './diff';
import type { ProposalEntry } from './use-builder-chat';

interface ProposalCardProps {
  entry: ProposalEntry;
  locale: string;
  onApply: (changeIds: string[]) => Promise<AgentBuilderApplyResult | null> | void;
  onDiscard: () => void;
  onRequestChanges: () => void;
}

/** Voice sample ▶︎ from /voice-samples/<voice>.mp3 (static, no API calls). */
function VoiceSample({ voice }: { voice: string }) {
  const t = useTranslations('agents.builder');
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => () => audioRef.current?.pause(), []);

  const toggle = () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    const audio = new Audio(`/voice-samples/${voice}.mp3`);
    audio.onended = () => setPlaying(false);
    audio.onerror = () => setPlaying(false);
    audioRef.current = audio;
    setPlaying(true);
    audio.play().catch(() => setPlaying(false));
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={playing}
      aria-label={playing ? t('stopSample') : t('playSample', { voice })}
      className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full border bg-background align-middle hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      {playing ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
    </button>
  );
}

function Row({ row }: { row: DiffRow }) {
  const t = useTranslations('agents.builder');
  if (row.long) {
    return (
      <div className="space-y-1">
        <div className="text-xs font-medium text-muted-foreground">{row.label}</div>
        {row.before !== undefined && (
          <div className="rounded-md bg-red-500/5 border border-red-500/20 px-2 py-1.5">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('before')}</div>
            <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground line-clamp-6">{row.before}</p>
          </div>
        )}
        <div className="rounded-md bg-emerald-500/5 border border-emerald-500/20 px-2 py-1.5">
          {row.before !== undefined && (
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('after')}</div>
          )}
          <p className="whitespace-pre-wrap break-words text-xs">{row.after}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
      <span className="font-medium text-muted-foreground">{row.label}:</span>
      {row.before !== undefined && (
        <>
          <span className="line-through decoration-red-500/60 text-muted-foreground break-words">{row.before}</span>
          <span aria-hidden="true">→</span>
          <span className="sr-only">{t('changesTo')}</span>
        </>
      )}
      <span className="font-medium break-words">{row.after}</span>
      {row.voice && <VoiceSample voice={row.voice} />}
    </div>
  );
}

function ChangeBody({ change, locale }: { change: AgentBuilderChange; locale: string }) {
  const t = useTranslations('agents.builder');
  const rows = describeChange(change, t);
  const runs = change.preview?.next_runs ?? [];
  return (
    <div className="space-y-1.5">
      {change.reason && <p className="text-xs text-muted-foreground">{change.reason}</p>}
      {rows.map((r) => (
        <Row key={r.key} row={r} />
      ))}
      {runs.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('nextRuns', { runs: runs.map((r) => formatRun(r, change.preview?.timezone, locale)).join(' · ') })}
        </p>
      )}
      {change.warnings?.map((w) => (
        <p key={w} className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          {w}
        </p>
      ))}
    </div>
  );
}

/** One proposal: per-change checkboxes, human-readable diff, Apply / Discard / Ask for changes. */
export function ProposalCard({ entry, locale, onApply, onDiscard, onRequestChanges }: ProposalCardProps) {
  const t = useTranslations('agents.builder');
  const { proposal, status, result, error } = entry;
  const createId = proposal.changes.find((c) => c.target === 'create_agent')?.id ?? null;
  const [selected, setSelected] = useState<Set<string>>(() => new Set(proposal.changes.map((c) => c.id)));
  const resultById = useMemo(() => new Map((result?.results ?? []).map((r) => [r.change_id, r])), [result]);

  const editable = status === 'pending' || status === 'failed' || (status === 'partial' && !!result?.remaining_proposal);
  const createMissing = !!createId && !selected.has(createId);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      // Everything else in a create proposal depends on the agent being created.
      if (id === createId) return next.has(id) ? new Set(proposal.changes.map((c) => c.id)) : new Set<string>();
      return next;
    });
  };

  const opLabel = (op: AgentBuilderChange['operation']) => t(`operation.${op}`);
  const count = selected.size;

  return (
    <section
      aria-label={t('proposalLabel', { summary: proposal.summary })}
      data-testid="proposal-card"
      className={`rounded-lg border bg-card p-3 space-y-3 ${status === 'discarded' ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold leading-snug">{proposal.summary}</h3>
        {status === 'applied' && <Badge className="shrink-0">{t('statusApplied')}</Badge>}
        {status === 'partial' && <Badge variant="outline" className="shrink-0">{t('statusPartial')}</Badge>}
        {status === 'discarded' && <Badge variant="outline" className="shrink-0">{t('statusDiscarded')}</Badge>}
      </div>

      {status !== 'discarded' && (
        <ul className="space-y-2">
          {proposal.changes.map((change) => {
            const subject = changeSubject(change);
            const res = resultById.get(change.id);
            const disabled = !editable || (!!createId && change.id !== createId && createMissing);
            const inputId = `${proposal.id}-${change.id}`;
            return (
              <li key={change.id} className="rounded-md border p-2.5">
                <div className="flex items-start gap-2">
                  {editable ? (
                    <input
                      id={inputId}
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      checked={selected.has(change.id)}
                      disabled={disabled}
                      onChange={() => toggle(change.id)}
                    />
                  ) : res ? (
                    res.ok ? (
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-label={t('resultOk')} />
                    ) : (
                      <CircleX className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-label={t('resultError')} />
                    )
                  ) : (
                    <span className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  )}
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <label htmlFor={inputId} className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                      {t(`parts.${change.part}`, { defaultValue: change.part })}
                      {subject && <span className="text-muted-foreground font-normal">· {subject}</span>}
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">{opLabel(change.operation)}</Badge>
                    </label>
                    <ChangeBody change={change} locale={locale} />
                    {res && !res.ok && res.error && <p className="text-xs text-destructive">{res.error}</p>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {createMissing && editable && <p className="text-xs text-muted-foreground">{t('createRequired')}</p>}
      {result && result.warnings.map((w) => (
        <p key={w} className="text-xs text-amber-700 dark:text-amber-400">{w}</p>
      ))}
      {status === 'applied' && <p role="status" className="text-xs text-emerald-700 dark:text-emerald-400">{t('appliedMessage', { count: result?.applied ?? 0 })}</p>}
      {status === 'partial' && result && (
        <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
          {t('partialMessage', { applied: result.applied, total: result.applied + result.failed })}
        </p>
      )}
      {status === 'failed' && <p role="alert" className="text-xs text-destructive">{error ?? t('applyFailed')}</p>}

      {editable && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => {
            const selectedChanges = proposal.changes.filter((c) => selected.has(c.id));
            const needsConfirmation = selectedChanges.some((c) => c.confirmation_required);
            if (needsConfirmation && !window.confirm(selectedChanges.flatMap((c) => c.warnings ?? []).join('\n\n'))) return;
            void onApply(selectedChanges.map((c) => c.id));
          }} disabled={count === 0}>
            {count === proposal.changes.length ? t('apply') : t('applySelected', { count })}
          </Button>
          <Button size="sm" variant="outline" onClick={onRequestChanges}>{t('requestChanges')}</Button>
          <Button size="sm" variant="ghost" onClick={onDiscard}>{t('discard')}</Button>
        </div>
      )}
      {status === 'applying' && (
        <p role="status" className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          {t('applying')}
        </p>
      )}
    </section>
  );
}
